import {
	resolvePersistedChartKind,
	PERSISTED_CHART_KIND,
	buildPersistedChartFilters,
	isPipelineBuiltinChartKind,
} from './statisticsPersistedCharts';
import { isFrozenChartSnapshot } from './statisticsChartRefreshMode';
import { normalizeTrendChartBundleKeys, resolveFrozenChartBundle } from './chartDataSnapshot';
import { buildCompositionChartBundle } from '../../composition/lib/buildCompositionChartBundle';
import { buildTrendChartBundle } from '../../trend/lib/buildTrendChartBundle';
import { buildOverdueChartBundle } from '../../overdue/lib/buildOverdueChartBundle';
import { collectOverdueOrdersForTable } from '../../overdue/lib/overdueStatChartData';
import { buildPipelineChartBundle } from '../../pipeline/lib/buildPipelineChartBundle';

/** 全年各月/各季 live 口径：是否已有可绘制的 periodYoy */
export const isYearMonthsLiveBundleRenderable = (bundle) => {
	if (!bundle?.trendYearMonthsMode) return true;
	if (bundle.trendChartLoading) return false;
	return Boolean(bundle.trendPeriodYoy?.years?.length);
};

const resolveLiveDisplayFallback = (snapshot, statisticsSourceSnapshotsById = {}) => {
	const rawFallback =
		snapshot?.liveDisplayFallback && typeof snapshot.liveDisplayFallback === 'object'
			? snapshot.liveDisplayFallback
			: (() => {
					const sourceId = snapshot?.sourceStatisticsChartId;
					if (!sourceId) return null;
					const sourceSnapshot = statisticsSourceSnapshotsById[sourceId];
					return sourceSnapshot?.dataSnapshot && typeof sourceSnapshot.dataSnapshot === 'object' ? sourceSnapshot.dataSnapshot : null;
				})();

	return rawFallback ? normalizeTrendChartBundleKeys(rawFallback) : null;
};

const mergeLiveWithDisplayFallback = (liveBundle, fallback) => {
	if (!fallback || isYearMonthsLiveBundleRenderable(liveBundle)) return liveBundle;

	return {
		...fallback,
		...liveBundle,
		trendPeriodYoy: liveBundle.trendPeriodYoy ?? fallback.trendPeriodYoy,
		compares: liveBundle.compares?.length ? liveBundle.compares : fallback.compares,
		trendYearMonthsMode: liveBundle.trendYearMonthsMode ?? fallback.trendYearMonthsMode,
		trendGranularity: liveBundle.trendGranularity ?? fallback.trendGranularity,
		trendMonthIndices: liveBundle.trendMonthIndices ?? fallback.trendMonthIndices,
		trendPeriodIndices: liveBundle.trendPeriodIndices ?? fallback.trendPeriodIndices,
		trendDualFullPeriod: liveBundle.trendDualFullPeriod ?? fallback.trendDualFullPeriod,
		trendDimensionCountSeries: liveBundle.trendDimensionCountSeries ?? fallback.trendDimensionCountSeries,
		trendDimensionTotalPriceSeries: liveBundle.trendDimensionTotalPriceSeries ?? fallback.trendDimensionTotalPriceSeries,
		trendDimensionSeriesByMetric: liveBundle.trendDimensionSeriesByMetric ?? fallback.trendDimensionSeriesByMetric,
		// 明细只跟当前 live 结果；fallback 可能来自源图快照，切图时会串台
		orders: liveBundle.orders ?? [],
		trendChartLoading: liveBundle.trendChartLoading,
		chartLoading: liveBundle.chartLoading,
	};
};

export const buildDashboardChartBundle = (params = {}) => {
	const chartKind = resolvePersistedChartKind(params.snapshot);
	if (chartKind === PERSISTED_CHART_KIND.COMPOSITION) {
		return buildCompositionChartBundle({
			snapshot: params.snapshot,
			filters: params.filters,
			primaryPhaseStats: params.primaryPhaseStats,
			selectedPhase: params.selectedPhase ?? null,
			isStatisticsQueryLoading: params.isStatisticsQueryLoading,
		});
	}

	return buildTrendChartBundle(params);
};

/** 已保存图表 → 展示用 bundle（冻结快照 / 逾期 / 实时查数） */
export const resolveDashboardChartBundleEntry = ({
	snapshot = null,
	primaryPhaseStats = null,
	overdueData = null,
	isOverdueLoading = false,
	fetchedSaleUsers = [],
	fetchedAssistantUsers = [],
	fetchedCustomers = [],
	isStatisticsQueryLoading = false,
	isStatisticsQueryFetching = false,
	forceLive = false,
	statisticsSourceSnapshotsById = {},
} = {}) => {
	if (!snapshot) return null;

	const frozenBundle =
		isFrozenChartSnapshot(snapshot) && snapshot.dataSnapshot ? resolveFrozenChartBundle(snapshot) : null;

	// 冻结快照缺全年走势数据时勿直接返回空图，改走 live 查数自愈
	const frozenYearMonthsBroken =
		Boolean(frozenBundle?.trendYearMonthsMode) && !frozenBundle?.trendPeriodYoy?.years?.length;

	if (!forceLive && frozenBundle && !frozenYearMonthsBroken) {
		if (resolvePersistedChartKind(snapshot) === PERSISTED_CHART_KIND.OVERDUE && !(frozenBundle.orders?.length)) {
			const sourceOverdue = frozenBundle.overdueData ?? overdueData;
			return {
				...frozenBundle,
				orders: collectOverdueOrdersForTable(sourceOverdue, frozenBundle.overdueConfig?.rangeMode),
			};
		}
		return frozenBundle;
	}

	const chartKind = resolvePersistedChartKind(snapshot);
	if (chartKind === PERSISTED_CHART_KIND.OVERDUE) {
		return buildOverdueChartBundle({ snapshot, overdueData, isOverdueLoading });
	}

	const liveDisplayFallback = resolveLiveDisplayFallback(snapshot, statisticsSourceSnapshotsById);

	if (isPipelineBuiltinChartKind(chartKind)) {
		const liveBundle = buildPipelineChartBundle({
			snapshot,
			primaryPhaseStats,
			isStatisticsQueryLoading,
			isStatisticsQueryFetching,
		});
		if (liveDisplayFallback && !primaryPhaseStats) {
			return {
				...liveDisplayFallback,
				...liveBundle,
				lensData: liveBundle.lensData ?? liveDisplayFallback.lensData,
				dimensionRows: liveBundle.dimensionRows?.length ? liveBundle.dimensionRows : liveDisplayFallback.dimensionRows,
				chartAggregates: liveBundle.chartAggregates ?? liveDisplayFallback.chartAggregates,
				orders: liveBundle.orders ?? [],
				chartLoading: liveBundle.chartLoading,
				isStatisticsQueryLoading,
				isStatisticsQueryFetching,
			};
		}
		return liveBundle;
	}

	const filters = buildPersistedChartFilters(snapshot);
	const liveBundle = buildDashboardChartBundle({
		snapshot,
		filters,
		primaryPhaseStats,
		selectedPhase: snapshot.selectedPhase ?? null,
		fetchedSaleUsers,
		fetchedAssistantUsers,
		fetchedCustomers,
		isStatisticsQueryLoading,
		isStatisticsQueryFetching,
	});

	// 冻结图点开明细会拉 live 订单：柱/图例必须跟明细同一套数，不能柱子停在定格、单数用最新
	if (frozenBundle && forceLive) {
		const isOrdersFetchPending =
			(isStatisticsQueryLoading || isStatisticsQueryFetching) && !(primaryPhaseStats?.orders?.length);

		if (isOrdersFetchPending || !isYearMonthsLiveBundleRenderable(liveBundle)) {
			return {
				...frozenBundle,
				orders: [],
				trendChartLoading: false,
				chartLoading: false,
				isStatisticsQueryLoading,
				isStatisticsQueryFetching,
			};
		}

		return {
			...frozenBundle,
			...liveBundle,
			orders: liveBundle.orders ?? [],
			trendChartLoading: false,
			chartLoading: false,
			isStatisticsQueryLoading,
			isStatisticsQueryFetching,
		};
	}

	const isOrdersFetchPending =
		forceLive &&
		(isStatisticsQueryLoading || isStatisticsQueryFetching) &&
		!(primaryPhaseStats?.orders?.length);

	if (isOrdersFetchPending) {
		const displayBundle = mergeLiveWithDisplayFallback(liveBundle, liveDisplayFallback) ?? frozenBundle ?? liveBundle;
		const chartRenderable = isYearMonthsLiveBundleRenderable(displayBundle);
		return {
			...displayBundle,
			orders: [],
			trendChartLoading: !chartRenderable,
			chartLoading: !chartRenderable && Boolean(displayBundle?.chartLoading),
			isStatisticsQueryLoading,
			isStatisticsQueryFetching,
		};
	}

	const merged = mergeLiveWithDisplayFallback(liveBundle, liveDisplayFallback);
	// 查数进行中且尚无主结果 / 全年走势仍不可画：保持骨架，避免标题先出、图区闪白
	const keepSkeletonWhilePending =
		(isStatisticsQueryLoading || isStatisticsQueryFetching) &&
		(!primaryPhaseStats || !isYearMonthsLiveBundleRenderable({ ...merged, trendChartLoading: false }));

	if (keepSkeletonWhilePending) {
		return {
			...merged,
			trendChartLoading: true,
			chartLoading: true,
			isStatisticsQueryLoading,
			isStatisticsQueryFetching,
		};
	}

	return {
		...merged,
		isStatisticsQueryLoading,
		isStatisticsQueryFetching,
	};
};
